import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { GlassCard } from './components/GlassCard';
import {
  Wallet,
  TrendingUp,
  Users,
  Shield,
  Zap,
  Target,
  Globe,
  Award,
  ArrowRight,
  CheckCircle2,
  DollarSign,
  Network,
  Calendar,
  ChevronDown,
  ChevronUp,
  Activity,
  BarChart3,
  Youtube,
  Send,
  Twitter,
  Instagram,
  Facebook
} from 'lucide-react';
import '@/App.css';

function App() {
  const [openFaq, setOpenFaq] = useState(null);

  const stats = [
    { label: 'Active Participants', value: '50,000+', icon: Users },
    { label: 'Transactions Processed', value: '$10M+', icon: TrendingUp },
    { label: 'System Accuracy', value: '99.9%', icon: Shield },
    { label: '24/7 Activity', value: 'Live', icon: Activity }
  ];

  const ecosystem = [
    {
      title: 'Direct Reward',
      description: 'Earn 5% on every direct referral with unlimited potential',
      icon: Wallet,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Smart Pool Reward',
      description: 'Structured pool system with reward distribution',
      icon: Target,
      color: 'from-violet-500 to-violet-600'
    },
    {
      title: 'Level-Based Reward',
      description: 'Multi-level earning based on network growth',
      icon: TrendingUp,
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      title: 'Network Bonus',
      description: 'Balanced upline and downline reward system',
      icon: Network,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Daily Reward',
      description: 'Activity-based daily earning depending on level',
      icon: Calendar,
      color: 'from-blue-600 to-violet-600'
    }
  ];

  const levels = [
    { name: 'Starter', price: 10 },
    { name: 'Basic', price: 15 },
    { name: 'Standard', price: 25 },
    { name: 'Advanced', price: 50, featured: true },
    { name: 'Pro', price: 100 },
    { name: 'Elite', price: 200, featured: true },
    { name: 'Master', price: 400 },
    { name: 'Titan', price: 800, featured: true }
  ];

  const smartPoolData = [
    { investment: '$100', pool: '$1,000', reward: '$20' },
    { investment: '$200', pool: '$2,000', reward: '$40' },
    { investment: '$400', pool: '$4,000', reward: '$80' },
    { investment: '$800', pool: '$8,000', reward: '$160' }
  ];

  const dailyRewards = [
    { level: '$100', percentage: '2.5%' },
    { level: '$200', percentage: '2.5%' },
    { level: '$400', percentage: '5%' },
    { level: '$800', percentage: '5%' }
  ];

  const steps = [
    { title: 'Register', description: 'Create your account', icon: Users },
    { title: 'Select Level', description: 'Choose participation tier', icon: Target },
    { title: 'Activate Account', description: 'Complete activation', icon: CheckCircle2 },
    { title: 'Build Network', description: 'Grow your community', icon: Globe },
    { title: 'Earn Rewards', description: 'Start earning daily', icon: DollarSign }
  ];

  const benefits = [
    'Structured earning system',
    'Transparent reward logic',
    'Scalable participation',
    'Multi-stream rewards',
    'Community-driven ecosystem'
  ];

  const whyZentra = [
    { title: 'Built for modern digital users', icon: Users },
    { title: 'Easy to understand', icon: CheckCircle2 },
    { title: 'Clean and scalable system', icon: TrendingUp },
    { title: 'Transparent structure', icon: Shield }
  ];

  const faqs = [
    {
      question: 'What is Zentra?',
      answer: 'Zentra is a structured digital ecosystem designed for scalable participation, transparent rewards, and long-term growth. It combines multiple reward mechanisms to create sustainable value for all participants.'
    },
    {
      question: 'How do I get started?',
      answer: 'Simply register your account, select your participation level, activate your account, and start building your network. You can begin earning through multiple streams including direct rewards, pool rewards, and daily earnings.'
    },
    {
      question: 'What are the participation levels?',
      answer: 'We offer 8 tiers ranging from Starter ($10) to Titan ($800). Each level provides different earning potential and benefits. You can start at any level and upgrade as you grow.'
    },
    {
      question: 'How are rewards distributed?',
      answer: 'Rewards are distributed through multiple mechanisms: 5% direct rewards, smart pool distributions, level-based earnings, network bonuses, and daily activity rewards. All distributions are automated and transparent.'
    },
    {
      question: 'Is there a minimum requirement?',
      answer: 'For daily rewards, you need a minimum of 2 active direct participants. Other reward mechanisms have no minimum requirements and start immediately upon participation.'
    },
    {
      question: 'How do I withdraw my earnings?',
      answer: 'Earnings can be withdrawn directly to your connected wallet at any time. There are no lock-up periods or withdrawal restrictions.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#05070d' }}>
      <Navbar />

      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758117169154-ba6ffd8f51ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdmlvbGV0JTIwZmx1aWQlMjBncmFkaWVudCUyMDNkJTIwc2hhcGV8ZW58MHx8fHwxNzc0MTIyMTUwfDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.3
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#05070d]/50 to-[#05070d] z-[1]" />

        <div className="absolute inset-0 z-[1] overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6" data-testid="hero-headline">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-violet-400 to-indigo-400">
                Build Smarter Digital Networks
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed" data-testid="hero-subheadline">
              Zentra is a structured digital ecosystem designed for scalable participation, transparent rewards, and long-term growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                className="rounded-full px-10 py-4 font-semibold text-lg text-white bg-gradient-to-r from-blue-600 to-violet-600 shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:shadow-[0_0_40px_rgba(59,130,246,0.7)] transition-all duration-300 w-full sm:w-auto"
                data-testid="hero-cta-primary"
              >
                Connect Wallet
              </button>
              <button
                className="rounded-full px-10 py-4 font-semibold text-lg text-white border border-white/20 hover:bg-white/5 transition-all duration-300 w-full sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                Explore System
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-20 max-w-4xl mx-auto"
          >
            <GlassCard className="text-center">
              <Users className="w-8 h-8 mx-auto mb-3 text-blue-400" />
              <div className="text-2xl font-bold mb-1">50K+</div>
              <div className="text-sm text-slate-400">Active Users</div>
            </GlassCard>
            <GlassCard className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-3 text-violet-400" />
              <div className="text-2xl font-bold mb-1">+125%</div>
              <div className="text-sm text-slate-400">Network Growth</div>
            </GlassCard>
            <GlassCard className="text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-3 text-indigo-400" />
              <div className="text-2xl font-bold mb-1">$10M+</div>
              <div className="text-sm text-slate-400">Rewards Distributed</div>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <GlassCard key={index} hover className="text-center" data-testid={`stat-card-${index}`}>
                <Icon className="w-10 h-10 mx-auto mb-4 text-blue-400" />
                <div className="text-3xl font-bold mb-2">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </GlassCard>
            );
          })}
        </div>
      </section>

      <section id="ecosystem" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="ecosystem-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="ecosystem-title">
            The Zentra Ecosystem
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Multiple reward mechanisms working together to create sustainable value
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ecosystem.map((item, index) => {
            const Icon = item.icon;
            return (
              <GlassCard key={index} hover className="group" data-testid={`ecosystem-card-${index}`}>
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </GlassCard>
            );
          })}
        </div>
      </section>

      <section id="participation" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="participation-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="participation-title">
            Participation Levels
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Choose your level and start building your network
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {levels.map((level, index) => (
            <GlassCard
              key={index}
              hover
              className={`text-center relative overflow-hidden ${level.featured ? 'border-blue-500/30' : ''}`}
              data-testid={`level-card-${index}`}
            >
              {level.featured && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-violet-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-xl">
                  Featured
                </div>
              )}
              <div className="text-sm uppercase tracking-widest text-slate-500 mb-3">{level.name}</div>
              <div className="text-4xl font-bold mb-2">${level.price}</div>
              <button className="mt-4 w-full rounded-full px-6 py-2 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300">
                Select
              </button>
            </GlassCard>
          ))}
        </div>
      </section>

      <section id="rewards" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="rewards-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="rewards-title">
            Reward System
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Transparent and structured reward distribution
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <GlassCard data-testid="direct-reward-card">
            <div className="flex items-center mb-6">
              <Wallet className="w-8 h-8 text-blue-400 mr-3" />
              <h3 className="text-2xl font-bold">Direct Reward</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-4 border-b border-white/5">
                <span className="text-slate-400">Commission Rate</span>
                <span className="text-2xl font-bold text-blue-400">5%</span>
              </div>
              <div className="flex items-center justify-between py-4">
                <span className="text-slate-400">Earning Potential</span>
                <span className="text-xl font-bold">Unlimited</span>
              </div>
            </div>
          </GlassCard>

          <GlassCard data-testid="level-reward-card">
            <div className="flex items-center mb-6">
              <TrendingUp className="w-8 h-8 text-violet-400 mr-3" />
              <h3 className="text-2xl font-bold">Level Rewards</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
                <span className="text-slate-400">Multi-level earning structure</span>
              </div>
              <div className="flex items-center text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
                <span className="text-slate-400">Based on network growth</span>
              </div>
              <div className="flex items-center text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
                <span className="text-slate-400">Scalable participation rewards</span>
              </div>
            </div>
          </GlassCard>
        </div>

        <GlassCard className="mb-12" data-testid="smart-pool-table">
          <h3 className="text-2xl font-bold mb-6 flex items-center">
            <Target className="w-7 h-7 text-violet-400 mr-3" />
            Smart Pool Rewards
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-400">Investment</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-400">Pool Target</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-400">Reward</th>
                </tr>
              </thead>
              <tbody>
                {smartPoolData.map((row, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-semibold">{row.investment}</td>
                    <td className="py-4 px-4 text-slate-400">{row.pool}</td>
                    <td className="py-4 px-4 font-bold text-blue-400">{row.reward}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        <GlassCard data-testid="daily-rewards-table">
          <h3 className="text-2xl font-bold mb-6 flex items-center">
            <Calendar className="w-7 h-7 text-indigo-400 mr-3" />
            Daily Rewards
          </h3>
          <div className="overflow-x-auto mb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-400">Level</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-400">Daily %</th>
                </tr>
              </thead>
              <tbody>
                {dailyRewards.map((row, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-semibold">{row.level}</td>
                    <td className="py-4 px-4 font-bold text-indigo-400">{row.percentage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-sm text-slate-300">
            <strong>Condition:</strong> Minimum 2 active direct participants required
          </div>
        </GlassCard>
      </section>

      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="how-it-works-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="how-it-works-title">
            How It Works
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Simple steps to start your journey
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <GlassCard hover className="text-center h-full">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 flex items-center justify-center font-bold text-sm shadow-lg">
                    {index + 1}
                  </div>
                  <Icon className="w-10 h-10 mx-auto mb-4 mt-6 text-blue-400" />
                  <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400">{step.description}</p>
                </GlassCard>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 z-10">
                    <ArrowRight className="w-6 h-6 text-blue-400/50" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section id="dashboard" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="dashboard-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="dashboard-title">
            Dashboard Preview
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Powerful analytics and real-time insights
          </p>
        </motion.div>

        <GlassCard className="p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500/10 to-violet-500/10 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-400">Total Earnings</span>
                <Wallet className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-3xl font-bold mb-1">$12,450</div>
              <div className="text-sm text-green-400">+24% this month</div>
            </div>

            <div className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-400">Network Size</span>
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div className="text-3xl font-bold mb-1">347</div>
              <div className="text-sm text-green-400">+52 this week</div>
            </div>

            <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-400">Active Level</span>
                <Award className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="text-3xl font-bold mb-1">Pro</div>
              <div className="text-sm text-slate-400">$100 tier</div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Activity Overview</h3>
              <BarChart3 className="w-6 h-6 text-slate-400" />
            </div>
            <div className="flex items-end justify-between h-48 gap-2">
              {[65, 45, 80, 55, 90, 70, 85].map((height, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-violet-600 rounded-t-lg transition-all duration-300 hover:from-blue-500 hover:to-violet-500"
                    style={{ height: `${height}%` }}
                  />
                  <span className="text-xs text-slate-500 mt-2">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-bold mb-4">Recent Activity</h4>
              <div className="space-y-3">
                {[
                  { action: 'Direct reward received', amount: '+$50', time: '2 hours ago' },
                  { action: 'Pool reward distributed', amount: '+$80', time: '5 hours ago' },
                  { action: 'New participant joined', amount: '', time: '1 day ago' }
                ].map((activity, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div>
                      <div className="text-sm font-medium">{activity.action}</div>
                      <div className="text-xs text-slate-500">{activity.time}</div>
                    </div>
                    {activity.amount && <div className="text-sm font-bold text-green-400">{activity.amount}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-bold mb-4">Network Growth</h4>
              <div className="relative h-32">
                <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,60 L28,50 L57,55 L85,35 L114,40 L142,25 L171,30 L200,15"
                    fill="none"
                    stroke="url(#lineGradient)"
                    strokeWidth="3"
                  />
                </svg>
              </div>
              <div className="text-sm text-slate-400 mt-4">Consistent growth over the last 30 days</div>
            </div>
          </div>
        </GlassCard>
      </section>

      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="benefits-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="benefits-title">
            Key Benefits
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {benefits.map((benefit, index) => (
            <GlassCard key={index} hover className="flex items-start" data-testid={`benefit-${index}`}>
              <CheckCircle2 className="w-6 h-6 text-green-400 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2">{benefit}</h3>
                <p className="text-slate-400">Built with transparency and scalability at its core for sustainable growth.</p>
              </div>
            </GlassCard>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="why-zentra-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="why-zentra-title">
            Why Zentra
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {whyZentra.map((item, index) => {
            const Icon = item.icon;
            return (
              <GlassCard key={index} hover className="flex items-center" data-testid={`why-zentra-${index}`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold">{item.title}</h3>
              </GlassCard>
            );
          })}
        </div>
      </section>

      <section id="faq" className="py-24 md:py-32 max-w-4xl mx-auto px-6 md:px-12" data-testid="faq-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <GlassCard key={index} className="cursor-pointer" data-testid={`faq-item-${index}`}>
              <div
                className="flex items-center justify-between"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <h3 className="text-lg font-bold pr-4">{faq.question}</h3>
                {openFaq === index ? (
                  <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </div>
              {openFaq === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-white/10 text-slate-400 leading-relaxed"
                >
                  {faq.answer}
                </motion.div>
              )}
            </GlassCard>
          ))}
        </div>
      </section>

      <section className="py-32 md:py-40 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-violet-600/20 to-indigo-600/20 blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="cta-title">
              Start Your Digital Growth Journey with Zentra
            </h2>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Join thousands of participants building sustainable networks and earning rewards
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                className="rounded-full px-12 py-5 font-bold text-lg text-white bg-gradient-to-r from-blue-600 to-violet-600 shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:shadow-[0_0_60px_rgba(59,130,246,0.8)] transition-all duration-300 w-full sm:w-auto"
                data-testid="cta-connect-wallet"
              >
                Connect Wallet
              </button>
              <button
                className="rounded-full px-12 py-5 font-bold text-lg text-white border-2 border-white/20 hover:bg-white/5 transition-all duration-300 w-full sm:w-auto"
                data-testid="cta-get-started"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-12">
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#ecosystem" className="hover:text-white transition-colors">Ecosystem</a></li>
                <li><a href="#participation" className="hover:text-white transition-colors">Levels</a></li>
                <li><a href="#rewards" className="hover:text-white transition-colors">Rewards</a></li>
                <li><a href="#dashboard" className="hover:text-white transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Licenses</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Social</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all" data-testid="social-youtube">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all" data-testid="social-telegram">
                  <Send className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all" data-testid="social-twitter">
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
              <div className="flex space-x-4 mt-4">
                <a href="#" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all" data-testid="social-instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all" data-testid="social-facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="font-['Outfit'] text-xl font-bold">ZENTRA</span>
              </div>
              <p className="text-slate-400 text-sm">
                © 2026 Zentra. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;